import { ClientFactory } from "@/main/common/ClientFactory";
import { MetaHandler } from "@/main/common/MetaHandler";
import { addPageResumeListener } from "@/main/common/PageActivity";

// The CSRF token is a server-render-time snapshot baked into the page (`_csrf` meta) and bound to the
// server session. On a long-open tab it can drift out of sync with the session, so the first write
// after a long idle period 403s and the user waits on the reactive self-heal (a refresh + one replay).
// This closes most of that window PROACTIVELY: when a tab that has been hidden for a while becomes
// visible again, refresh the token before the user gets a chance to submit. It complements — does not
// replace — the reactive self-heal in ClientFactory.
// See designs/04-live/2026-06-07-csrf-robustness-self-healing-token-refresh-design.md.

// Only refresh after a genuinely long hidden stretch, so quick tab flips don't fire a GET on every
// switch. Two minutes comfortably covers "walked away and came back" without being chatty.
const STALE_AFTER_HIDDEN_MS = 2 * 60_000;

let teardown: (() => void) | null = null;
let hiddenAt: number | null = null;

export function setupProactiveCsrfRefresh(): void {
    if (typeof document === "undefined" || teardown) {
        return;
    }

    const onVisibilityChange = () => {
        if (document.visibilityState === "hidden") {
            hiddenAt = Date.now();
        }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    const removeResumeListener = addPageResumeListener(() => {
        const hiddenForMs = hiddenAt == null ? 0 : Date.now() - hiddenAt;
        hiddenAt = null;

        // Only after a long hidden stretch, and only for a signed-in viewer (anonymous pages have no
        // session token worth pre-warming). refreshCsrfToken de-dupes concurrent calls and swallows its
        // own failures, so this is best-effort and cannot throw into the resume path.
        if (hiddenForMs < STALE_AFTER_HIDDEN_MS || !MetaHandler.isUserLoggedIn()) {
            return;
        }
        void ClientFactory.refreshCsrfToken();
    });

    teardown = () => {
        document.removeEventListener("visibilitychange", onVisibilityChange);
        removeResumeListener();
        hiddenAt = null;
    };
}

export function resetProactiveCsrfRefreshForTests(): void {
    if (teardown) {
        teardown();
        teardown = null;
    }
    hiddenAt = null;
}
